import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import { SessionStorageService } from '../services/session-storage.service';
import { BusinessEventNotificationTeam, BusinessEventRecipientEmployee } from './business-events.model';
import { BusinessEventsService } from './business-events.service';

@Component({
  selector: 'app-business-event-teams',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, ButtonModule, DialogModule, IftaLabelModule, InputGroupModule,
    InputGroupAddonModule, InputTextModule, ProgressBarModule, TableModule, TagModule, ToastModule,
    ToggleSwitchModule, TooltipModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './business-event-teams.component.html',
  styleUrl: './business-event-teams.component.css'
})
export class BusinessEventTeamsComponent implements OnInit {
  private readonly events = inject(BusinessEventsService);
  private readonly messages = inject(MessageService);
  private readonly permissions = inject(PermissionService);
  private readonly session = inject(SessionStorageService);

  teams: BusinessEventNotificationTeam[] = [];
  employees: BusinessEventRecipientEmployee[] = [];
  metrics: PageMetric[] = [];
  isLoading = false;
  isSaving = false;
  canManage = false;
  dialogVisible = false;
  error = '';
  employeeSearch = '';
  form: BusinessEventNotificationTeam = this.emptyTeam();

  ngOnInit(): void {
    this.canManage = this.permissions.hasPermission(app_permissions.business_events.manage);
    this.load();
  }

  get companyId(): number {
    return this.session.getCompany()?.id ?? this.events.getCompanyId();
  }

  get companyName(): string {
    return this.session.getCompany()?.name || this.session.getCustomer()?.company?.name || `Company #${this.companyId}`;
  }

  get selectedEmployees(): BusinessEventRecipientEmployee[] {
    const selectedIds = new Set(this.form.memberEmployeeIds || []);
    return this.employees.filter(employee => selectedIds.has(employee.employeeId));
  }

  get filteredEmployees(): BusinessEventRecipientEmployee[] {
    const query = this.employeeSearch.trim().toLocaleLowerCase();
    if (!query) return this.employees;
    return this.employees.filter(employee => [employee.displayName, employee.employeeNumber, employee.emailAddress, employee.phoneNumber]
      .some(value => value?.toLocaleLowerCase().includes(query)));
  }

  load(): void {
    this.isLoading = true;
    forkJoin({
      teams: this.events.getNotificationTeams(this.companyId),
      employees: this.canManage ? this.events.getRecipientEmployees(this.companyId) : of([])
    }).subscribe({
      next: data => {
        this.teams = data.teams || [];
        this.employees = data.employees || [];
        this.metrics = [
          { label: 'Teams', value: this.teams.length, tone: 'info' },
          { label: 'Active teams', value: this.teams.filter(team => team.active !== false).length, tone: 'success' },
          { label: 'Active memberships', value: this.teams.reduce((total, team) => total + (team.memberCount || 0), 0), tone: 'info' }
        ];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messages.add({ severity: 'error', summary: 'Notification teams', detail: 'Unable to load company teams and employees.' });
      }
    });
  }

  openTeam(team?: BusinessEventNotificationTeam): void {
    this.error = '';
    this.employeeSearch = '';
    this.form = team
      ? { ...team, companyId: this.companyId, memberEmployeeIds: [...(team.memberEmployeeIds || [])] }
      : this.emptyTeam();
    this.dialogVisible = true;
  }

  isEmployeeSelected(employeeId: number): boolean {
    return (this.form.memberEmployeeIds || []).includes(employeeId);
  }

  toggleEmployee(employeeId: number): void {
    const selected = new Set(this.form.memberEmployeeIds || []);
    if (selected.has(employeeId)) selected.delete(employeeId); else selected.add(employeeId);
    this.form.memberEmployeeIds = [...selected];
  }

  employeeInitials(employee: BusinessEventRecipientEmployee): string {
    return employee.displayName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase() || 'E';
  }

  employeeContact(employee: BusinessEventRecipientEmployee): string {
    const contacts = [employee.emailAddress, employee.phoneNumber].filter((value): value is string => !!value?.trim());
    return contacts.length ? contacts.join(' · ') : 'No email or phone configured';
  }

  save(): void {
    if (!this.form.teamName.trim()) {
      this.error = 'A team name is required.';
      return;
    }
    this.isSaving = true;
    this.error = '';
    this.events.saveNotificationTeam({
      ...this.form,
      companyId: this.companyId,
      memberEmployeeIds: [...new Set(this.form.memberEmployeeIds || [])]
    }).subscribe({
      next: () => {
        this.isSaving = false;
        this.dialogVisible = false;
        this.messages.add({ severity: 'success', summary: 'Notification team saved' });
        this.load();
      },
      error: response => {
        this.isSaving = false;
        this.error = response?.error?.message || 'Unable to save the notification team.';
      }
    });
  }

  private emptyTeam(): BusinessEventNotificationTeam {
    return { companyId: this.companyId, teamName: '', active: true, memberEmployeeIds: [] };
  }
}
